/**
 * GRC Copilot Sidebar — Phase 1 Action Bridge wired
 * Persistent AI chat side panel with contextual awareness.
 * Parses [COPILOT_ACTION] JSON blocks from SSE stream and executes them
 * via the ActionBridge SDK.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { CompanionWindow } from '@/components/shared/companion/CompanionWindow'
import { TaskTray, useCompanionTasks } from '@/components/shared/companion/TaskTray'
import { useCompanionWindowStore } from '@/stores/useCompanionWindowStore'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  Sparkles,
  X,
  Send,
  Bot,
  User,
  Loader2,
  Copy,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  Navigation2,
  Undo2,
  Eye,
  Compass,
  ArrowRight,
  ShieldX,
  Clock,
  Circle,
  Square,
  Save,
  BrainCircuit,
  MessageSquare,
  Wand2,
  Plus,
  ListTodo,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip'
import { RagAnalysisPanel } from '@/components/shared/knowledge/RagAnalysisPanel'
import { copilotApi, type CopilotMessage, type FeatureMatch } from '@/services/copilot.service'
import { useCopilotAction } from '@/lib/copilot/CopilotActionProvider'
import type { UiAction } from '@/lib/copilot/action-bridge'
import { classifyAction } from '@/lib/copilot/action-guard'
import type { CopilotPreviewPayload } from '@/lib/copilot/HITLGate'
import {
  executeBackendTool,
  guardBackendTool,
  type BackendToolCall,
  type ToolResult,
} from '@/lib/copilot/tool-executor'
import type { ChainPreviewPayload } from '@/lib/copilot/ProcessPreview'
import {
  flowRecorder,
  onRecorderChange,
  toBackendSteps,
  type RecordedFlow,
} from '@/lib/copilot/flow-recorder'
import { createFlow } from '@/services/rpa-flows.service'
import { CopilotAgentPanel } from '@/components/shared/copilot/CopilotAgentPanel'
import { openAndFillForm } from '@/lib/copilot/agent-runner'
import { LlmModeSelector, useLlmCreditGuard } from '@/components/shared/copilot/LlmModeSelector'
import { LlmTierSelector } from '@/components/shared/copilot/LlmTierSelector'
import { notifyCreditExhausted } from '@/components/shared/copilot/AiCreditGate'
import { CreditBreakerActions, isCreditBreakerText } from '@/components/shared/copilot/CreditBreakerActions'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'

// ── Props ────────────────────────────────────────────────────────────────────

export interface GrcCopilotSidebarProps {
  moduleContext: string
  pageContext: string
  entityData?: Record<string, unknown>
}

// ── Conversation persistence ──────────────────────────────────────────────────
// The backend persists each exchange (CopilotConversation/CopilotMessageDB), but
// the in-memory `messages` state is lost on a full page reload. We mirror the
// current session's messages to localStorage so a reload restores the chat
// (offline-safe; a backend rehydrate would be nicer but is out of scope).

const CONVERSATION_KEY = 'sgrc-copilot-conversation'
const MAX_PERSISTED_MESSAGES = 50

function loadPersistedMessages(): CopilotMessage[] {
  try {
    const raw = localStorage.getItem(CONVERSATION_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as CopilotMessage[]) : []
  } catch {
    return []
  }
}

// ── Quick Actions ────────────────────────────────────────────────────────────

const QUICK_ACTIONS = [
  { label: 'Résumer', prompt: 'Résume les informations clés de cette page.' },
  { label: 'Analyser', prompt: 'Analyse les données affichées et identifie les points importants.' },
  { label: 'Recommander', prompt: 'Quelles actions recommandes-tu en priorité ?' },
  { label: 'Expliquer', prompt: 'Explique en détail le contexte et les implications.' },
]

// ── Navigation intent detection ──────────────────────────────────────────────

const NAV_INTENT_PATTERNS = [
  /\b(où|where|comment accéder|aller à|aller a|ouvre|go to|find|trouver|naviguer|navigate|accéder|acceder|cherche|je cherche|comment aller)\b/i,
]
const NAV_FEATURE_KEYWORDS = [
  /\b(risque|risk|conformit|complian|gouvernan|audit|securit|securite|incident|politique|policy|pia|aipd|maturit|strateg|change|vendor|fourn|donnee|data|ia\b|ai\b|esg|dashboard|bilan|rapport|matrice|registre|controle|evaluation|questionnaire|framework|referentiel|preuve|kri|bcp|okr|adkar)\b/i,
]

function hasNavigationIntent(message: string): boolean {
  return NAV_INTENT_PATTERNS.some((p) => p.test(message)) &&
    NAV_FEATURE_KEYWORDS.some((p) => p.test(message))
}

// ── Executed action record (for inline confirmation display) ──────────────────

interface ExecutedAction {
  msgId: string
  action: UiAction | { op: 'backendTool' | 'backendChain' }
  ok: boolean
  detail?: string
  /** For navigate — show undo button for 5s */
  canUndo?: boolean
  undoAt?: number
  /** Shown when Action Guard blocked the action */
  refused?: boolean
  refuseReason?: string
  /** Shown while waiting for HITL confirmation */
  pending?: boolean
}

// ── [COPILOT_ACTION] parser ───────────────────────────────────────────────────

/**
 * Extract all action JSON objects from accumulated SSE text.
 * Returns the list of parsed UiAction objects (invalid JSON is silently skipped).
 */
function extractActions(text: string): UiAction[] {
  const actions: UiAction[] = []
  const re = /\[COPILOT_ACTION\]:\s*(\{[^}]*\})/g
  let match: RegExpExecArray | null
  while ((match = re.exec(text)) !== null) {
    try {
      const parsed = JSON.parse(match[1]) as UiAction
      if (parsed && typeof parsed.op === 'string') {
        actions.push(parsed)
      }
    } catch {
      // malformed JSON — skip
    }
  }
  return actions
}

/**
 * Extract the balanced JSON literal (object or array) that immediately follows
 * `marker:` in `text`, starting the scan at `fromIndex`. Returns the raw JSON
 * string and the index just past it, or null if unbalanced/missing.
 */
function extractBalancedJson(
  text: string,
  startIdx: number,
): { json: string; end: number } | null {
  // Skip whitespace after the colon
  let i = startIdx
  while (i < text.length && /\s/.test(text[i])) i++
  const open = text[i]
  if (open !== '{' && open !== '[') return null
  const close = open === '{' ? '}' : ']'

  let depth = 0
  let inStr = false
  let esc = false
  for (let j = i; j < text.length; j++) {
    const ch = text[j]
    if (inStr) {
      if (esc) esc = false
      else if (ch === '\\') esc = true
      else if (ch === '"') inStr = false
      continue
    }
    if (ch === '"') inStr = true
    else if (ch === open) depth++
    else if (ch === close) {
      depth--
      if (depth === 0) return { json: text.slice(i, j + 1), end: j + 1 }
    }
  }
  return null // unbalanced (stream not complete yet)
}

/** Extract all [COPILOT_TOOL] single backend-tool calls (balanced JSON). */
function extractTools(text: string): BackendToolCall[] {
  const out: BackendToolCall[] = []
  const re = /\[COPILOT_TOOL\]:/g
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    const parsed = extractBalancedJson(text, re.lastIndex)
    if (!parsed) continue
    try {
      const obj = JSON.parse(parsed.json) as BackendToolCall
      if (obj && typeof obj.capability_code === 'string' && typeof obj.path === 'string') {
        out.push(obj)
      }
    } catch {
      /* incomplete / malformed — skip */
    }
  }
  return out
}

/** Extract all [COPILOT_CHAIN] step arrays (balanced JSON). */
function extractChains(text: string): BackendToolCall[][] {
  const out: BackendToolCall[][] = []
  const re = /\[COPILOT_CHAIN\]:/g
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    const parsed = extractBalancedJson(text, re.lastIndex)
    if (!parsed) continue
    try {
      const arr = JSON.parse(parsed.json) as BackendToolCall[]
      if (Array.isArray(arr) && arr.length > 0 && arr.every((s) => s && typeof s.capability_code === 'string')) {
        out.push(arr)
      }
    } catch {
      /* skip */
    }
  }
  return out
}

/**
 * Strip [COPILOT_ACTION] / [COPILOT_TOOL] / [COPILOT_CHAIN] blocks from visible
 * text so the chat bubble doesn't display raw JSON to the user.
 */
function stripActionBlocks(text: string): string {
  let out = text.replace(/\[COPILOT_ACTION\]:\s*\{[^}]*\}/g, '')
  // Strip balanced TOOL / CHAIN blocks
  for (const marker of ['[COPILOT_TOOL]:', '[COPILOT_CHAIN]:']) {
    let idx = out.indexOf(marker)
    while (idx !== -1) {
      const parsed = extractBalancedJson(out, idx + marker.length)
      if (!parsed) break
      out = out.slice(0, idx) + out.slice(parsed.end)
      idx = out.indexOf(marker)
    }
  }
  return out.trim()
}

// ── Markdown renderer (lightweight) ──────────────────────────────────────────

function renderMarkdown(text: string): React.ReactNode {
  const lines = text.split('\n')
  const elements: React.ReactNode[] = []

  lines.forEach((line, i) => {
    if (line.startsWith('### ')) {
      elements.push(<h4 key={i} className="font-semibold text-sm mt-2 mb-1">{line.slice(4)}</h4>)
    } else if (line.startsWith('## ')) {
      elements.push(<h3 key={i} className="font-bold text-sm mt-3 mb-1">{line.slice(3)}</h3>)
    } else if (line.startsWith('# ')) {
      elements.push(<h2 key={i} className="font-bold text-base mt-3 mb-1">{line.slice(2)}</h2>)
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      elements.push(
        <li key={i} className="ml-4 text-sm list-disc">{renderInlineMarkdown(line.slice(2))}</li>
      )
    } else if (/^\d+\.\s/.test(line)) {
      const content = line.replace(/^\d+\.\s/, '')
      elements.push(
        <li key={i} className="ml-4 text-sm list-decimal">{renderInlineMarkdown(content)}</li>
      )
    } else if (line.startsWith('```')) {
      // skip delimiters
    } else if (line.trim() === '') {
      elements.push(<br key={i} />)
    } else {
      elements.push(<p key={i} className="text-sm mb-1">{renderInlineMarkdown(line)}</p>)
    }
  })

  return <>{elements}</>
}

function renderInlineMarkdown(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={i} className="bg-muted px-1 py-0.5 rounded text-xs font-mono">
          {part.slice(1, -1)}
        </code>
      )
    }
    return part
  })
}

// ── Typing Indicator ─────────────────────────────────────────────────────────

function TypingIndicator() {
  return (
    <div className="flex items-center gap-2 p-3">
      <Avatar className="h-7 w-7">
        <AvatarFallback className="bg-primary/10 text-primary">
          <Bot className="h-4 w-4" />
        </AvatarFallback>
      </Avatar>
      <div className="flex gap-1 items-center">
        <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:0ms]" />
        <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:150ms]" />
        <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:300ms]" />
      </div>
    </div>
  )
}

// ── Action Confirmation Chip ─────────────────────────────────────────────────

function ActionChip({
  executed,
  onUndo,
}: {
  executed: ExecutedAction
  onUndo: () => void
}) {
  const [showUndo, setShowUndo] = useState(executed.canUndo ?? false)

  useEffect(() => {
    if (!executed.canUndo) return
    const ms = (executed.undoAt ?? Date.now() + 5000) - Date.now()
    if (ms <= 0) { setShowUndo(false); return }
    const t = setTimeout(() => setShowUndo(false), ms)
    return () => clearTimeout(t)
  }, [executed.canUndo, executed.undoAt])

  // Refused chip
  if (executed.refused) {
    return (
      <div className="flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded-md text-xs bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800">
        <ShieldX className="h-3 w-3 shrink-0" />
        <span className="flex-1 truncate">
          Action refusée{executed.refuseReason ? ` : ${executed.refuseReason}` : ''}
        </span>
      </div>
    )
  }

  // Pending chip
  if (executed.pending) {
    return (
      <div className="flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded-md text-xs bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
        <Clock className="h-3 w-3 shrink-0 animate-pulse" />
        <span className="flex-1 truncate">En attente de confirmation…</span>
      </div>
    )
  }

  const label = (() => {
    const a = executed.action
    switch (a.op) {
      case 'navigate': return `Navigué vers ${(a as Extract<UiAction, { op: 'navigate' }>).to}`
      case 'highlight': return `Mis en évidence : ${(a as Extract<UiAction, { op: 'highlight' }>).selector}`
      case 'readContext': return 'Contexte lu'
      case 'scrollTo': return `Défilé vers : ${(a as Extract<UiAction, { op: 'scrollTo' }>).selector}`
      case 'openTab': return `Onglet ouvert : ${(a as Extract<UiAction, { op: 'openTab' }>).tabId}`
      case 'fillForm': return `Formulaire rempli`
      case 'setField': return `Champ mis à jour`
      case 'submitForm': return `Formulaire soumis`
      default: return executed.detail ?? 'Action effectuée'
    }
  })()

  const Icon = executed.action.op === 'navigate'
    ? Navigation2
    : executed.action.op === 'highlight'
    ? Eye
    : Sparkles

  return (
    <div className={cn(
      'flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded-md text-xs',
      executed.ok
        ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
        : 'bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800',
    )}>
      <Icon className="h-3 w-3 shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      {showUndo && (
        <button
          onClick={onUndo}
          className="flex items-center gap-0.5 text-xs underline underline-offset-2 opacity-80 hover:opacity-100"
        >
          <Undo2 className="h-3 w-3" />
          Annuler
        </button>
      )}
    </div>
  )
}

// ── Main Component ───────────────────────────────────────────────────────────

export function GrcCopilotSidebar({ moduleContext, pageContext, entityData }: GrcCopilotSidebarProps) {
  const navigate = useNavigate()
  const { execute: bridgeExecute } = useCopilotAction()
  const { guardDialog, ensureSufficientCredit } = useLlmCreditGuard()

  // ── Companion window chrome (Phase 1 subsystem A) — persisted per user ──────
  // open/minimized/dock/position/size + Assistant-vs-Agent mode + active tab all
  // live in useCompanionWindowStore (localStorage 'sgrc-companion-window').
  const open = useCompanionWindowStore((s) => s.open)
  const minimized = useCompanionWindowStore((s) => s.minimized)
  const activeTab = useCompanionWindowStore((s) => s.activeTab)
  const companionMode = useCompanionWindowStore((s) => s.mode)
  const badge = useCompanionWindowStore((s) => s.badge)
  const badgeCount = useCompanionWindowStore((s) => s.badgeCount)
  const openWindow = useCompanionWindowStore((s) => s.openWindow)
  const closeWindow = useCompanionWindowStore((s) => s.closeWindow)
  const restoreWindow = useCompanionWindowStore((s) => s.restore)
  const minimizeWindow = useCompanionWindowStore((s) => s.minimize)
  const setActiveTab = useCompanionWindowStore((s) => s.setActiveTab)

  // Task tray polling (subsystem E) — runs while the Companion is open OR
  // minimized so the pill badge can light up on task completion / HITL.
  const tasksQuery = useCompanionTasks(open || minimized)
  const trayTasks = tasksQuery.data?.tasks ?? []
  const traySource = tasksQuery.data?.source ?? 'none'

  // HITL pending while minimized → badge on the pill.
  useEffect(() => {
    const onPreview = () => {
      const s = useCompanionWindowStore.getState()
      if (s.open && s.minimized) s.setBadge('hitl')
    }
    window.addEventListener('copilot:preview', onPreview)
    return () => window.removeEventListener('copilot:preview', onPreview)
  }, [])

  const [showRag, setShowRag] = useState(false)
  // "Créer avec l'IA" inline prompt (smart-create quick action).
  const [showCreateInput, setShowCreateInput] = useState(false)
  const [createInputValue, setCreateInputValue] = useState('')
  // Rehydrate the previous session's conversation on mount (see CONVERSATION_KEY).
  const [messages, setMessages] = useState<CopilotMessage[]>(loadPersistedMessages)
  const [inputValue, setInputValue] = useState('')
  // Credit breaker: when a response is paused/stopped for lack of credits, hold
  // the blocked message id + the last user text so the user can Retry/Continue/Stop.
  const [creditBlock, setCreditBlock] = useState<{ msgId: string; lastText: string } | null>(null)
  const [isTyping, setIsTyping] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)

  // Navigate-to-feature chips
  const [navMatches, setNavMatches] = useState<FeatureMatch[]>([])
  const [showNavInput, setShowNavInput] = useState(false)
  const [navInputValue, setNavInputValue] = useState('')

  // Executed actions keyed by assistantMsgId
  const [executedActions, setExecutedActions] = useState<Record<string, ExecutedAction[]>>({})

  // Phase 3b — Flow Recorder state
  const [isRecording, setIsRecording] = useState<boolean>(flowRecorder.isRecording())
  const [recordedStepCount, setRecordedStepCount] = useState<number>(flowRecorder.count())
  const [saveFlowOpen, setSaveFlowOpen] = useState(false)
  const [capturedFlow, setCapturedFlow] = useState<RecordedFlow | null>(null)
  const [flowName, setFlowName] = useState('')
  const [flowDescription, setFlowDescription] = useState('')
  const [savingFlow, setSavingFlow] = useState(false)
  const [saveFlowError, setSaveFlowError] = useState<string | null>(null)
  const [saveFlowSuccess, setSaveFlowSuccess] = useState<string | null>(null)

  // Subscribe to recorder changes so the badge + counter stay live.
  useEffect(() => {
    const unsub = onRecorderChange((recording) => {
      setIsRecording(recording)
      setRecordedStepCount(flowRecorder.count())
    })
    return unsub
  }, [])

  const handleToggleRecording = useCallback(() => {
    if (flowRecorder.isRecording()) {
      const flow = flowRecorder.stop()
      setCapturedFlow(flow)
      setFlowName('')
      setFlowDescription('')
      setSaveFlowError(null)
      setSaveFlowSuccess(null)
      setSaveFlowOpen(true)
    } else {
      flowRecorder.start()
    }
  }, [])

  const handleSaveFlow = useCallback(async () => {
    if (!capturedFlow) return
    if (!flowName.trim()) {
      setSaveFlowError('Le nom du flux est requis.')
      return
    }
    setSavingFlow(true)
    setSaveFlowError(null)
    try {
      const steps = toBackendSteps(capturedFlow)
      const created = await createFlow({
        name: flowName.trim(),
        description: flowDescription.trim(),
        trigger: 'manual',
        steps,
        requires_confirmation: true,
      })
      setSaveFlowSuccess(`Flux "${created.name}" enregistré (${steps.length} étape(s)).`)
      setTimeout(() => setSaveFlowOpen(false), 1200)
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 404) {
        setSaveFlowError(
          "Backend RPA non encore déployé (404). Le flux sera disponible après la reconstruction de governance-service."
        )
      } else {
        setSaveFlowError("Échec de l'enregistrement du flux.")
      }
    } finally {
      setSavingFlow(false)
    }
  }, [capturedFlow, flowName, flowDescription])

  // Tracks which actions have already been fired (de-dupe across SSE token callbacks)
  const firedActionsRef = useRef<Set<string>>(new Set())

  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages, isTyping])

  // Persist the conversation to localStorage so a full page reload restores it.
  // Skipped while streaming to avoid per-token writes; the final settled state is
  // written when `isStreaming` flips back to false.
  useEffect(() => {
    if (isStreaming) return
    try {
      if (messages.length === 0) {
        localStorage.removeItem(CONVERSATION_KEY)
      } else {
        localStorage.setItem(
          CONVERSATION_KEY,
          JSON.stringify(messages.slice(-MAX_PERSISTED_MESSAGES)),
        )
      }
    } catch {
      /* ignore quota / serialization errors */
    }
  }, [messages, isStreaming])

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300)
    }
  }, [open])

  /** Fire any new [COPILOT_ACTION] blocks found in accumulated text. De-duplicated. */
  const fireActions = useCallback(
    async (msgId: string, accumulatedText: string) => {
      const actions = extractActions(accumulatedText)
      for (const action of actions) {
        const key = `${msgId}:${JSON.stringify(action)}`
        if (firedActionsRef.current.has(key)) continue
        firedActionsRef.current.add(key)

        // ── Action Guard classification ──────────────────────────────────────
        const guardResult = classifyAction(action)

        // Guard blocked (not allowed) — show inline refusal chip, never execute
        if (!guardResult.allowed) {
          setExecutedActions((prev) => ({
            ...prev,
            [msgId]: [
              ...(prev[msgId] ?? []),
              {
                msgId,
                action,
                ok: false,
                refused: true,
                refuseReason: guardResult.reason ?? 'Action refusée par le garde-fou.',
                detail: guardResult.reason,
              },
            ],
          }))
          continue
        }

        // Read action — execute immediately, no confirm
        if (!guardResult.requiresConfirm) {
          const result = await bridgeExecute(action)
          setExecutedActions((prev) => ({
            ...prev,
            [msgId]: [
              ...(prev[msgId] ?? []),
              {
                msgId,
                action,
                ok: result.ok,
                detail: result.detail,
                canUndo: result.ok && action.op === 'navigate',
                undoAt: result.ok && action.op === 'navigate' ? Date.now() + 5000 : undefined,
              },
            ],
          }))
          continue
        }

        // Write/destructive/outward — emit preview event, wait for HITL
        const requestId = `${msgId}:${action.op}:${Date.now()}`

        // Show pending chip while waiting
        setExecutedActions((prev) => ({
          ...prev,
          [msgId]: [
            ...(prev[msgId] ?? []),
            { msgId, action, ok: false, pending: true, detail: 'En attente de confirmation…' },
          ],
        }))

        // Proposed values for diff display (fillForm / setField)
        const proposedValues: Record<string, unknown> =
          action.op === 'fillForm'
            ? ((action as Record<string, unknown>).values as Record<string, unknown>) ?? {}
            : action.op === 'setField'
            ? { [(action as Record<string, unknown>).field as string]: (action as Record<string, unknown>).value }
            : {}

        const payload: CopilotPreviewPayload = {
          requestId,
          actions: [action],
          guardResult,
          currentValues: {},
          proposedValues,
          onConfirm: async (_editedValues) => {
            // If user edited values, apply them to the action
            const finalAction =
              _editedValues && Object.keys(_editedValues).length > 0
                ? { ...action, values: _editedValues }
                : action
            const result = await bridgeExecute(finalAction as UiAction)

            setExecutedActions((prev) => {
              const list = (prev[msgId] ?? []).filter((ex) => !(ex.pending && ex.action === action))
              return {
                ...prev,
                [msgId]: [
                  ...list,
                  {
                    msgId,
                    action,
                    ok: result.ok,
                    detail: result.detail,
                    canUndo: result.ok && action.op === 'navigate',
                    undoAt: result.ok && action.op === 'navigate' ? Date.now() + 5000 : undefined,
                  },
                ],
              }
            })
          },
          onCancel: () => {
            setExecutedActions((prev) => {
              const list = (prev[msgId] ?? []).filter((ex) => !(ex.pending && ex.action === action))
              return {
                ...prev,
                [msgId]: [
                  ...list,
                  {
                    msgId,
                    action,
                    ok: false,
                    detail: 'Action annulée par l\'utilisateur.',
                  },
                ],
              }
            })
          },
        }

        window.dispatchEvent(new CustomEvent('copilot:preview', { detail: payload }))
      }
    },
    [bridgeExecute],
  )

  // ── [COPILOT_TOOL] — single backend tool call ────────────────────────────────
  const renderToolResult = useCallback(
    (msgId: string, removePending: boolean, result: ToolResult, code: string) => {
      setExecutedActions((prev) => {
        const list = removePending
          ? (prev[msgId] ?? []).filter((ex) => !ex.pending)
          : (prev[msgId] ?? [])
        const idSuffix = result.data?.id ? ` : id=${result.data.id}` : ''
        return {
          ...prev,
          [msgId]: [
            ...list,
            {
              msgId,
              action: { op: 'backendTool' },
              ok: result.ok,
              detail: result.ok
                ? `${code.split('.').slice(-1)[0]} (${result.status})${idSuffix}`
                : `Échec ${result.status} : ${result.error ?? 'erreur'}`,
            },
          ],
        }
      })
    },
    [],
  )

  const fireTools = useCallback(
    async (msgId: string, accumulatedText: string) => {
      const tools = extractTools(accumulatedText)
      for (const tool of tools) {
        const key = `${msgId}:TOOL:${JSON.stringify(tool)}`
        if (firedActionsRef.current.has(key)) continue
        firedActionsRef.current.add(key)

        const guard = guardBackendTool(tool)

        // Read or session-approved write — execute immediately.
        if (guard.allowed && !guard.requiresConfirm) {
          const result = await executeBackendTool(tool)
          renderToolResult(msgId, false, result, tool.capability_code)
          continue
        }

        // Blocked outright (shouldn't happen — guard allows w/ confirm) → refusal.
        if (!guard.allowed) {
          setExecutedActions((prev) => ({
            ...prev,
            [msgId]: [
              ...(prev[msgId] ?? []),
              { msgId, action: { op: 'backendTool' }, ok: false, refused: true, refuseReason: guard.reason },
            ],
          }))
          continue
        }

        // Write/destructive/outward → HITL preview, execute on confirm.
        setExecutedActions((prev) => ({
          ...prev,
          [msgId]: [...(prev[msgId] ?? []), { msgId, action: { op: 'backendTool' }, ok: false, pending: true }],
        }))

        const payload: CopilotPreviewPayload = {
          requestId: `${msgId}:TOOL:${tool.capability_code}:${Date.now()}`,
          actions: [
            {
              op: 'submitToExternalSystem',
              tool_name: tool.capability_code,
              side_effect: guard.actionClass,
              // describeAction fallback shows the op; include readable hint
              system: `${(tool.method ?? '').toUpperCase()} ${tool.path}`,
            } as unknown as UiAction,
          ],
          guardResult: guard,
          currentValues: {},
          proposedValues: (tool.body as Record<string, unknown>) ?? {},
          onConfirm: async (edited) => {
            const finalTool: BackendToolCall =
              edited && Object.keys(edited).length > 0
                ? { ...tool, body: { ...(tool.body ?? {}), ...edited } }
                : tool
            const result = await executeBackendTool(finalTool)
            renderToolResult(msgId, true, result, tool.capability_code)
          },
          onCancel: () => {
            setExecutedActions((prev) => ({
              ...prev,
              [msgId]: [
                ...(prev[msgId] ?? []).filter((ex) => !ex.pending),
                { msgId, action: { op: 'backendTool' }, ok: false, detail: 'Action annulée par l\'utilisateur.' },
              ],
            }))
          },
        }
        window.dispatchEvent(new CustomEvent('copilot:preview', { detail: payload }))
      }
    },
    [renderToolResult],
  )

  // ── [COPILOT_CHAIN] — multi-step process ─────────────────────────────────────
  const fireChains = useCallback(
    (msgId: string, accumulatedText: string) => {
      const chains = extractChains(accumulatedText)
      for (const steps of chains) {
        const key = `${msgId}:CHAIN:${JSON.stringify(steps)}`
        if (firedActionsRef.current.has(key)) continue
        firedActionsRef.current.add(key)

        const payload: ChainPreviewPayload = {
          requestId: `${msgId}:CHAIN:${Date.now()}`,
          steps,
          onComplete: (results) => {
            const okCount = results.filter((r) => r.ok).length
            const failed = results.find((r) => !r.ok)
            setExecutedActions((prev) => ({
              ...prev,
              [msgId]: [
                ...(prev[msgId] ?? []),
                {
                  msgId,
                  action: { op: 'backendChain' },
                  ok: !failed,
                  detail: failed
                    ? `Processus arrêté : ${okCount}/${steps.length} étape(s) réussie(s)`
                    : `Processus terminé : ${okCount}/${steps.length} étape(s) réussie(s)`,
                },
              ],
            }))
          },
          onCancel: () => {
            setExecutedActions((prev) => ({
              ...prev,
              [msgId]: [
                ...(prev[msgId] ?? []),
                { msgId, action: { op: 'backendChain' }, ok: false, detail: 'Processus annulé.' },
              ],
            }))
          },
        }
        window.dispatchEvent(new CustomEvent('copilot:chainPreview', { detail: payload }))
      }
    },
    [],
  )

  // "Créer avec l'IA" — resolve target module, open + pre-fill its create form
  // through the HITL diff (reusable openAndFillForm helper).
  const handleAiCreate = useCallback(async (prompt: string) => {
    if (!prompt.trim()) return
    setShowCreateInput(false)
    setCreateInputValue('')
    // Minimize (instead of closing) so the Companion pill stays reachable and
    // can badge the HITL confirmation while the form is being pre-filled.
    minimizeWindow()
    const creditOk = await ensureSufficientCredit(5)
    if (!creditOk) return
    await openAndFillForm(moduleContext, prompt.trim())
  }, [ensureSufficientCredit, moduleContext, minimizeWindow])

  const handleNavSearch = useCallback(async (query: string) => {
    if (!query.trim()) return
    const matches = await copilotApi.navigateToFeature(query, 'client')
    setNavMatches(matches)
    setShowNavInput(false)
    setNavInputValue('')
  }, [])

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isTyping) return
      setCreditBlock(null)  // clear any prior breaker banner on a fresh send

      // AI-credit pre-flight: if the active LLM mode (commercial) is out of
      // credit, prompt the user to switch to the free OSS mode before calling
      // the LLM. Aborts the send if the user declines.
      const creditOk = await ensureSufficientCredit(5)
      if (!creditOk) return

      // Navigation intent: check before calling LLM
      if (hasNavigationIntent(text)) {
        copilotApi.navigateToFeature(text, 'client').then((matches) => {
          if (matches.length > 0 && matches[0].score >= 0.75) {
            setNavMatches(matches.slice(0, 3))
          }
        }).catch(() => { /* silent */ })
      } else {
        setNavMatches([])
      }

      const userMsg: CopilotMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content: text.trim(),
        timestamp: new Date().toISOString(),
      }

      const assistantMsgId = crypto.randomUUID()

      setMessages((prev) => [...prev, userMsg])
      setInputValue('')
      setIsTyping(true)
      setIsStreaming(true)

      const emptyAssistantMsg: CopilotMessage = {
        id: assistantMsgId,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
      }
      setMessages((prev) => [...prev, emptyAssistantMsg])

      let accumulated = ''

      try {
        await copilotApi.chatStream(
          text.trim(),
          // `mode` (assistant|agent, subsystem H) is an additive hint the
          // backend can start honoring for autonomy policy; ignored today.
          { moduleContext, pageContext, entityData, page_data: entityData, mode: companionMode },
          [...messages, userMsg],
          (token: string) => {
            accumulated += token
            // Check for new action blocks on each token
            fireActions(assistantMsgId, accumulated)
            fireTools(assistantMsgId, accumulated)
            fireChains(assistantMsgId, accumulated)

            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId ? { ...m, content: accumulated } : m
              )
            )
          },
          () => {
            // Stream complete — one final pass
            fireActions(assistantMsgId, accumulated)
            fireTools(assistantMsgId, accumulated)
            fireChains(assistantMsgId, accumulated)
            setIsStreaming(false)
            setIsTyping(false)
            // Mid-task credit exhaustion: the orchestrator pauses/stops the flow
            // and emits a marker in the response. Surface a toast + the inline
            // Retry / Continue / Stop banner under the blocked message.
            if (isCreditBreakerText(accumulated)) {
              setCreditBlock({ msgId: assistantMsgId, lastText: text.trim() })
              notifyCreditExhausted(navigate,
                'Tâche IA interrompue — crédits épuisés. Achetez des crédits pour reprendre.')
            }
          },
          (error: string) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId
                  ? { ...m, content: `Erreur: ${error}` }
                  : m
              )
            )
            setIsStreaming(false)
            setIsTyping(false)
          },
        )
      } catch {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsgId
              ? { ...m, content: 'Désolé, une erreur est survenue. Veuillez réessayer.' }
              : m
          )
        )
        setIsStreaming(false)
        setIsTyping(false)
      }
    },
    [messages, moduleContext, pageContext, entityData, isTyping, fireActions, fireTools, fireChains, ensureSufficientCredit, companionMode, navigate],
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    sendMessage(inputValue)
  }

  const handleQuickAction = (prompt: string) => {
    sendMessage(prompt)
  }

  const handleCopyMessage = (content: string) => {
    navigator.clipboard.writeText(content)
  }

  const handleClearChat = () => {
    setMessages([])
    setExecutedActions({})
    firedActionsRef.current.clear()
  }

  const handleFeedback = useCallback(
    async (messageId: string, rating: 'positive' | 'negative') => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, feedback: rating } : m
        )
      )
      try {
        await copilotApi.submitFeedback(messageId, rating, { moduleContext, pageContext })
      } catch {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId ? { ...m, feedback: undefined } : m
          )
        )
      }
    },
    [moduleContext, pageContext],
  )

  // RAG scope — derive an entity from entityData when the host page provides one.
  const ragEntityType =
    typeof entityData?.entity_type === 'string' ? (entityData.entity_type as string) : undefined
  const ragEntityId =
    typeof entityData?.entity_id === 'string'
      ? (entityData.entity_id as string)
      : typeof entityData?.id === 'string'
      ? (entityData.id as string)
      : undefined

  return (
    <>
      {/* Floating launcher — shown while the Companion window is closed. */}
      {!open && !minimized && (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={openWindow}
              // Offset above the FloatingMenu (z-50) so the Copilot FAB is never
              // overlapped/intercepted, and sits above all page content (z-[60]).
              className="group fixed bottom-[5.5rem] right-6 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 ring-1 ring-primary/20 transition-all hover:scale-105 hover:shadow-xl"
              aria-label="Assistant IA"
              data-copilot-id="copilot-fab"
            >
              {/* Pulsing halo so users notice the assistant. */}
              <span className="pointer-events-none absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/40 opacity-60" />
              {/* Live dot badge. */}
              <span className="absolute -right-0.5 -top-0.5 inline-flex h-3.5 w-3.5 animate-pulse rounded-full bg-emerald-400 ring-2 ring-background" />
              <Sparkles className="relative h-6 w-6" />
              <span className="sr-only">Ouvrir l&apos;assistant IA</span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="left" className="font-medium">
            Assistant IA
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      )}

      {/* Minimized pill — compact status chip; badge lights on task completion
          or pending HITL; click restores the window. Never blocks the page. */}
      {open && minimized && (
        <div
          className="fixed bottom-[5.5rem] right-6 z-[70] flex items-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 ring-1 ring-primary/20"
          data-copilot-id="companion-pill"
        >
          <button
            onClick={restoreWindow}
            className="flex h-11 items-center gap-2 rounded-l-full pl-4 pr-1 transition-transform hover:scale-[1.02]"
            aria-label="Restaurer l'assistant IA"
            data-copilot-id="companion-pill-restore"
          >
            <Sparkles className="h-4 w-4" />
            <span className="text-sm font-medium">Copilot</span>
            {(badgeCount > 0 || badge != null) && (
              <span
                data-copilot-id="companion-pill-badge"
                className={cn(
                  'inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1 text-[10px] font-bold',
                  badge === 'hitl'
                    ? 'animate-pulse bg-amber-400 text-amber-950'
                    : 'bg-emerald-400 text-emerald-950',
                )}
              >
                {badge === 'hitl' ? '!' : badgeCount > 0 ? badgeCount : '✓'}
              </span>
            )}
          </button>
          <button
            onClick={closeWindow}
            className="flex h-11 items-center rounded-r-full pl-1 pr-3 opacity-70 hover:opacity-100"
            aria-label="Fermer l'assistant IA"
            data-copilot-id="companion-pill-close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Companion window (Phase 1 subsystem A) — draggable/dockable/resizable
          chrome re-housing the existing copilot body. */}
      {open && !minimized && (
        <CompanionWindow
          headerActions={
            <>
              <Button
                variant={showRag ? 'secondary' : 'ghost'}
                size="icon"
                className="h-7 w-7"
                onClick={() => setShowRag((v) => !v)}
                title="Analyser avec l'IA (RAG)"
                data-copilot-id="copilot-rag-toggle"
              >
                <BrainCircuit className="h-3.5 w-3.5" />
                <span className="sr-only">Analyser avec l'IA</span>
              </Button>
              <Button
                variant={isRecording ? 'destructive' : 'ghost'}
                size="icon"
                className="h-7 w-7"
                onClick={handleToggleRecording}
                title={isRecording ? 'Arrêter et sauvegarder le flux' : 'Enregistrer ce flux'}
                data-copilot-id="copilot-record-toggle"
              >
                {isRecording ? (
                  <Square className="h-3.5 w-3.5" />
                ) : (
                  <Circle className="h-3.5 w-3.5" />
                )}
                <span className="sr-only">
                  {isRecording ? 'Arrêter l’enregistrement' : 'Enregistrer ce flux'}
                </span>
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleClearChat} title="Nouvelle conversation">
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
            </>
          }
        >

          {/* LLM provider mode (Commercial vs Open Source) */}
          <div className="shrink-0 px-4 py-2 border-b flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">Modèle IA</span>
            <LlmModeSelector />
          </div>

          {/* LLM tier + model (Premium / Équilibré / Économique / OSS) */}
          <div className="shrink-0 px-4 py-2 border-b">
            <LlmTierSelector />
          </div>

          {/* RAG grounded analysis (toggled from the header) */}
          {showRag && (
            <div className="shrink-0 border-b bg-muted/20 p-3 max-h-[45%] overflow-y-auto">
              <RagAnalysisPanel
                module={moduleContext}
                entityType={ragEntityType}
                entityId={ragEntityId}
                source="advise"
                embedded
              />
            </div>
          )}

          {guardDialog}

          {/* Context Badge */}
          <div className="shrink-0 px-4 py-2 border-b bg-muted/30">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary" className="text-xs">
                {moduleContext}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {pageContext}
              </Badge>
            </div>
          </div>

          {/* Chat / Agent / Tasks tabs — chat+agent stay in sync with the
              header Assistant/Agent mode toggle (subsystem H). */}
          <div className="shrink-0 px-4 py-2 border-b">
            <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted p-1">
              <button
                onClick={() => setActiveTab('chat')}
                className={cn(
                  'flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                  activeTab === 'chat'
                    ? 'bg-background shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                data-copilot-id="copilot-tab-chat"
              >
                <MessageSquare className="h-3.5 w-3.5" />
                Discussion
              </button>
              <button
                onClick={() => setActiveTab('agent')}
                className={cn(
                  'flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                  activeTab === 'agent'
                    ? 'bg-background shadow-sm text-primary'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                data-copilot-id="copilot-tab-agent"
              >
                <Bot className="h-3.5 w-3.5" />
                Agent
              </button>
              <button
                onClick={() => setActiveTab('tasks')}
                className={cn(
                  'relative flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                  activeTab === 'tasks'
                    ? 'bg-background shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                data-copilot-id="copilot-tab-tasks"
              >
                <ListTodo className="h-3.5 w-3.5" />
                Tâches
                {(badgeCount > 0 || badge != null) && activeTab !== 'tasks' && (
                  <span
                    className={cn(
                      'absolute -right-0.5 -top-0.5 inline-flex h-3.5 min-w-[0.875rem] items-center justify-center rounded-full px-0.5 text-[9px] font-bold',
                      badge === 'hitl' ? 'bg-amber-400 text-amber-950' : 'bg-emerald-400 text-emerald-950',
                    )}
                    data-copilot-id="copilot-tab-tasks-badge"
                  >
                    {badge === 'hitl' ? '!' : badgeCount || ''}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* ── Agent mode: plan + act ─────────────────────────────────────── */}
          {activeTab === 'agent' && (
            <CopilotAgentPanel
              moduleContext={moduleContext}
              pageContext={pageContext}
              ensureSufficientCredit={ensureSufficientCredit}
            />
          )}

          {/* ── Task tray (subsystem E): Companion/agentic task list ────────── */}
          {activeTab === 'tasks' && (
            <TaskTray
              tasks={trayTasks}
              source={traySource}
              isLoading={tasksQuery.isLoading || tasksQuery.isFetching}
            />
          )}

          {/* Recording indicator (Phase 3b) */}
          {isRecording && (
            <div
              className="shrink-0 px-4 py-2 border-b bg-red-50 dark:bg-red-950/30 flex items-center gap-2"
              data-copilot-id="copilot-recording-banner"
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600" />
              </span>
              <span className="text-xs font-medium text-red-700 dark:text-red-300">
                Enregistrement du flux… {recordedStepCount} étape(s) capturée(s)
              </span>
            </div>
          )}

          {/* Messages Area */}
          {activeTab === 'chat' && (
          <div className="flex-1 min-h-0 overflow-hidden">
            <ScrollArea className="h-full">
              <div ref={scrollRef} className="p-4 space-y-4">
                {messages.length === 0 && (
                  <div className="text-center py-8 space-y-3">
                    <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Bot className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Bienvenue dans GRC Copilot</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Posez vos questions sur {moduleContext} ou utilisez les actions rapides ci-dessous.
                      </p>
                    </div>
                  </div>
                )}

                {messages.map((msg) => {
                  const visibleContent = msg.role === 'assistant'
                    ? stripActionBlocks(msg.content)
                    : msg.content
                  const msgExecuted = executedActions[msg.id] ?? []

                  return (
                    <div
                      key={msg.id}
                      className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {msg.role === 'assistant' && (
                        <Avatar className="h-7 w-7 shrink-0">
                          <AvatarFallback className="bg-primary/10 text-primary">
                            <Bot className="h-4 w-4" />
                          </AvatarFallback>
                        </Avatar>
                      )}

                      <div
                        className={`group relative max-w-[85%] rounded-lg px-3 py-2 ${
                          msg.role === 'user'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        }`}
                      >
                        {msg.role === 'user' ? (
                          <p className="text-sm">{visibleContent}</p>
                        ) : (
                          <div className="prose-sm">
                            {renderMarkdown(visibleContent)}
                            {isStreaming && msg.id === messages[messages.length - 1]?.id && (
                              <span className="inline-block w-2 h-4 bg-primary/70 animate-pulse ml-0.5 align-text-bottom" />
                            )}
                          </div>
                        )}

                        {/* Inline action confirmation chips */}
                        {msgExecuted.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {msgExecuted.map((ex, idx) => (
                              <ActionChip
                                key={idx}
                                executed={ex}
                                onUndo={() => navigate(-1)}
                              />
                            ))}
                          </div>
                        )}

                        {msg.role === 'assistant' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="absolute -right-8 top-0 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => handleCopyMessage(visibleContent)}
                            title="Copier"
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        )}

                        {/* Sources */}
                        {msg.metadata?.sources && Array.isArray(msg.metadata.sources) && msg.metadata.sources.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-border/50">
                            <p className="text-xs text-muted-foreground mb-1">Sources:</p>
                            {(msg.metadata.sources as { title: string; url?: string }[]).map((src, i) => (
                              <span key={i} className="text-xs text-primary/80 mr-2">
                                {src.url ? (
                                  <a href={src.url} className="hover:underline">{src.title}</a>
                                ) : (
                                  src.title
                                )}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Feedback Buttons */}
                        {msg.role === 'assistant' && msg.content && (
                          <div className="flex items-center gap-1 mt-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className={cn("h-6 w-6 p-0", msg.feedback === 'positive' && "text-green-500")}
                              onClick={() => handleFeedback(msg.id, 'positive')}
                            >
                              <ThumbsUp className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={cn("h-6 w-6 p-0", msg.feedback === 'negative' && "text-red-500")}
                              onClick={() => handleFeedback(msg.id, 'negative')}
                            >
                              <ThumbsDown className="h-3 w-3" />
                            </Button>
                          </div>
                        )}

                        {/* Credit breaker: Retry / Continue / Stop */}
                        {creditBlock?.msgId === msg.id && (
                          <CreditBreakerActions
                            onRetry={() => { const t = creditBlock.lastText; setCreditBlock(null); if (t) sendMessage(t) }}
                            onStop={() => setCreditBlock(null)}
                          />
                        )}
                      </div>

                      {msg.role === 'user' && (
                        <Avatar className="h-7 w-7 shrink-0">
                          <AvatarFallback className="bg-secondary">
                            <User className="h-4 w-4" />
                          </AvatarFallback>
                        </Avatar>
                      )}
                    </div>
                  )
                })}

                {isTyping && !isStreaming && <TypingIndicator />}
              </div>
            </ScrollArea>
          </div>
          )}

          {/* Navigation Chips (shown after navigate-intent message) */}
          {activeTab === 'chat' && navMatches.length > 0 && (
            <div className="shrink-0 px-4 py-2 border-t bg-primary/5">
              <p className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
                <Compass className="h-3 w-3" />
                Voulez-vous naviguer vers&nbsp;?
              </p>
              <div className="flex flex-col gap-1">
                {navMatches.map((match) => (
                  <Button
                    key={match.feature_id}
                    variant="outline"
                    size="sm"
                    className="w-full text-left justify-between text-xs h-8 rounded-lg border-primary/30 hover:bg-primary/10"
                    onClick={() => { navigate(match.route); closeWindow() }}
                  >
                    <span className="font-medium truncate">{match.title_fr}</span>
                    <div className="flex items-center gap-1.5 shrink-0 ml-2">
                      <Badge variant="secondary" className="text-[10px] h-4">{match.module}</Badge>
                      <ArrowRight className="h-3 w-3 text-primary" />
                    </div>
                  </Button>
                ))}
                <button
                  className="text-[10px] text-muted-foreground/60 hover:text-muted-foreground text-left mt-0.5"
                  onClick={() => setNavMatches([])}
                >
                  Masquer les suggestions
                </button>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          {activeTab === 'chat' && messages.length === 0 && (
            <div className="shrink-0 px-4 py-2 border-t bg-muted/20">
              <div className="flex flex-wrap gap-1.5">
                {QUICK_ACTIONS.map((action) => (
                  <Button
                    key={action.label}
                    variant="outline"
                    size="sm"
                    className="text-xs h-7 rounded-full"
                    onClick={() => handleQuickAction(action.prompt)}
                  >
                    {action.label}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs h-7 rounded-full gap-1"
                  onClick={() => setShowNavInput((v) => !v)}
                >
                  <Compass className="h-3 w-3" />
                  Trouver
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs h-7 rounded-full gap-1 border-primary/40 text-primary"
                  onClick={() => setShowCreateInput((v) => !v)}
                  data-copilot-id="copilot-ai-create-quick"
                >
                  <Wand2 className="h-3 w-3" />
                  Créer avec l'IA
                </Button>
              </div>
              {showNavInput && (
                <div className="mt-2 flex gap-1.5">
                  <Input
                    autoFocus
                    value={navInputValue}
                    onChange={(e) => setNavInputValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleNavSearch(navInputValue)
                      if (e.key === 'Escape') setShowNavInput(false)
                    }}
                    placeholder="Rechercher une fonctionnalité..."
                    className="text-xs h-7 flex-1"
                  />
                  <Button
                    size="sm"
                    className="h-7 px-2"
                    onClick={() => handleNavSearch(navInputValue)}
                    disabled={!navInputValue.trim()}
                  >
                    <ArrowRight className="h-3 w-3" />
                  </Button>
                </div>
              )}
              {showCreateInput && (
                <div className="mt-2 flex gap-1.5">
                  <Input
                    autoFocus
                    value={createInputValue}
                    onChange={(e) => setCreateInputValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAiCreate(createInputValue)
                      if (e.key === 'Escape') setShowCreateInput(false)
                    }}
                    placeholder="Ex : créer un risque de dépendance à un fournisseur cloud unique"
                    className="text-xs h-7 flex-1"
                    data-copilot-id="copilot-ai-create-input"
                  />
                  <Button
                    size="sm"
                    className="h-7 px-2"
                    onClick={() => handleAiCreate(createInputValue)}
                    disabled={!createInputValue.trim()}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Input Area */}
          {activeTab === 'chat' && (
          <div className="shrink-0 border-t p-3">
            <form onSubmit={handleSubmit} className="flex gap-2">
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Posez votre question..."
                className="flex-1 text-sm"
                disabled={isTyping}
              />
              <Button
                type="submit"
                size="icon"
                className="shrink-0"
                disabled={!inputValue.trim() || isTyping}
              >
                {isTyping ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </form>
          </div>
          )}
        </CompanionWindow>
      )}

      {/* Phase 3b — Save session as RPA Flow dialog */}
      <Dialog open={saveFlowOpen} onOpenChange={setSaveFlowOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="h-4 w-4 text-primary" />
              Sauvegarder comme flux RPA
            </DialogTitle>
            <DialogDescription>
              {capturedFlow
                ? `${capturedFlow.steps.length} étape(s) capturée(s). Donnez un nom à ce flux pour le rejouer plus tard.`
                : 'Aucune étape capturée.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="flow-name">Nom du flux</Label>
              <Input
                id="flow-name"
                data-copilot-id="save-flow-name"
                value={flowName}
                onChange={(e) => setFlowName(e.target.value)}
                placeholder="Ex. Inscription TPS/TVQ client"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="flow-description">Description</Label>
              <Textarea
                id="flow-description"
                data-copilot-id="save-flow-description"
                value={flowDescription}
                onChange={(e) => setFlowDescription(e.target.value)}
                placeholder="Décrivez ce que ce flux automatise…"
                rows={3}
              />
            </div>

            {capturedFlow && capturedFlow.steps.length > 0 && (
              <div className="rounded-md border bg-muted/30 p-2 max-h-32 overflow-auto">
                <p className="text-xs font-medium mb-1 text-muted-foreground">Étapes capturées</p>
                <ol className="text-xs space-y-0.5 list-decimal list-inside">
                  {capturedFlow.steps.map((s) => (
                    <li key={s.order} className="truncate">
                      {s.label ?? s.op ?? s.tool}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {saveFlowError && (
              <p className="text-xs text-amber-600 dark:text-amber-400">{saveFlowError}</p>
            )}
            {saveFlowSuccess && (
              <p className="text-xs text-emerald-600 dark:text-emerald-400">{saveFlowSuccess}</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveFlowOpen(false)} disabled={savingFlow}>
              Annuler
            </Button>
            <Button
              onClick={handleSaveFlow}
              disabled={savingFlow || !flowName.trim()}
              data-copilot-id="save-flow-submit"
            >
              {savingFlow ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default GrcCopilotSidebar
